import React from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { IClientObjectModel } from 'interfaces';

// Палитра карточки клиента: рамки, разделители, подписи, вторичный текст, подложки.
export const BORDER_COLOR = '#e6e8ec';
export const DIVIDER_COLOR = '#eef0f3';
export const LABEL_COLOR = '#6b7385';
export const SECONDARY_COLOR = '#4a5263';
export const SUBSTRATE_COLOR = '#f7f8fa';

export interface IPillColors {
  bg: string;
  color: string;
}

export const PILL_GREEN: IPillColors = { bg: '#d6f5e3', color: '#1a6b40' };
export const PILL_YELLOW: IPillColors = { bg: '#fdf3d3', color: '#7a5f0c' };
export const PILL_GREY: IPillColors = { bg: DIVIDER_COLOR, color: SECONDARY_COLOR };
// Синяя плашка: цвет текста — primary темы.
export const PILL_BLUE_BG = '#e8effa';

export const useCardStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    color: theme.palette.primary.main,
  },
  sectionIcon: {
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginLeft: 6,
  },
  card: {
    backgroundColor: '#fff',
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: 12,
    overflow: 'hidden',
  },
  fieldLabel: {
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: LABEL_COLOR,
    lineHeight: 1.3,
  },
  fieldValue: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1a1d1f',
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  fieldEmpty: {
    color: LABEL_COLOR,
  },
  pill: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 16,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
}));

export const isEmptyValue = (value: React.ReactNode): boolean => value === null || value === undefined || value === '';

// Цвета кружка NPS по логике промоутер/нейтрал/критик.
export const getNpsColors = (score: number): IPillColors => {
  if (score >= 9) return { bg: '#d6f5e3', color: '#1a7f4b' };
  if (score >= 7) return { bg: '#fdf3d3', color: '#9a7b15' };
  return { bg: '#fde2e2', color: '#b3261e' };
};

export const getNpsColorsOrEmpty = (score: number | null): IPillColors =>
  score === null ? PILL_GREY : getNpsColors(score);

export const formatDate = (value?: string | null): string | null => (value ? String(value).slice(0, 10) : null);

// Дата в виде ДД.MM.YYYY. Переставляем части ISO-строки, а не парсим в Date, —
// иначе день у полуночных значений сдвигался бы из-за таймзоны.
export const formatDateDots = (value?: string | null): string | null => {
  const date = formatDate(value);
  if (!date) return null;
  const parts = date.split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : date;
};

const formatDiscountValue = (percentage: number | null, fixedAmount: number | null): string | null => {
  if (percentage) return `${percentage}%`;
  if (fixedAmount) return String(fixedAmount);
  return null;
};

export const formatDiscount = (object: IClientObjectModel): string | null => {
  if (!object.discounts || object.discounts.length === 0) return null;
  const parts = object.discounts
    .map((discount) => formatDiscountValue(discount.discountPercentage, discount.fixedAmount))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
};

// Сравнение guid без учёта регистра: источники отдают их по-разному.
export const sameGuid = (left?: string | null, right?: string | null): boolean =>
  !!left && !!right && left.toLowerCase() === right.toLowerCase();

interface ISectionProps {
  icon: React.ReactNode;
  title: string;
}

export const Section: React.FC<ISectionProps> = ({ icon, title, children }) => {
  const classes = useCardStyles();
  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography className={classes.sectionTitle}>{title}</Typography>
      </Box>
      {children}
    </Box>
  );
};

interface IFieldProps {
  label: string;
  value: React.ReactNode;
}

// Подпись над значением; пустое значение — «-».
export const Field: React.FC<IFieldProps> = ({ label, value }) => {
  const classes = useCardStyles();
  const empty = isEmptyValue(value);
  return (
    <Box>
      <Typography className={classes.fieldLabel} style={{ marginBottom: 4 }}>
        {label}
      </Typography>
      <Box className={empty ? `${classes.fieldValue} ${classes.fieldEmpty}` : classes.fieldValue}>
        {empty ? '-' : value}
      </Box>
    </Box>
  );
};

interface IPillProps {
  label: string;
  colors: IPillColors;
  className?: string;
}

export const Pill: React.FC<IPillProps> = ({ label, colors, className }) => {
  const classes = useCardStyles();
  return (
    <span
      className={className ? `${classes.pill} ${className}` : classes.pill}
      style={{ backgroundColor: colors.bg, color: colors.color }}
    >
      {label}
    </span>
  );
};

export const EmptyDash: React.FC = () => {
  const classes = useCardStyles();
  return <Typography className={`${classes.fieldValue} ${classes.fieldEmpty}`}>-</Typography>;
};
